import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { WhatsAppIcon } from '@/components/WhatsAppButton';
import { openWhatsApp, WhatsAppTemplates } from '@/utils/whatsapp';
import { 
  Plus, 
  MapPin,
  Calendar,
  CheckCircle,
  Circle,
  Navigation,
  Clock,
  User,
  Route,
  Trash2,
  Edit,
  Eye,
  X,
  GripVertical,
  Map,
  ExternalLink,
  Bell,
  BellRing,
  MessageCircle
} from 'lucide-react';
import { format, startOfWeek, endOfWeek, getWeek } from 'date-fns';
import { pt } from 'date-fns/locale';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const visitedIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const pendingIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const headquartersIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const numberIcon = (number) => L.divIcon({
  className: 'custom-number-icon',
  html: `<div style="background-color: #3b82f6; color: white; border-radius: 50%; width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 12px; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);">${number}</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14]
});

const ROUTE_TYPES = [
  { id: 'diaria', label: 'Diária' },
  { id: 'semanal', label: 'Semanal' },
  { id: 'mensal', label: 'Mensal' }
];

function MapController({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, 12);
    }
  }, [center, map]);
  return null;
}

export default function RoutesPage() {
  const { api, user } = useAuth();
  const [routes, setRoutes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [viewingRoute, setViewingRoute] = useState(null);
  const [editingRoute, setEditingRoute] = useState(null);
  const [checkInClient, setCheckInClient] = useState(null);
  const [checkInNotes, setCheckInNotes] = useState('');
  const [userLocation, setUserLocation] = useState(null);
  const [activeTab, setActiveTab] = useState('list');
  const [mapCenter, setMapCenter] = useState([39.5, -8.0]); // Portugal center
  const [nearbyAlerts, setNearbyAlerts] = useState(true); // GPS proximity alerts
  const [nearbyClient, setNearbyClient] = useState(null);
  
  const isAdmin = user?.role === 'admin';
  
  const [form, setForm] = useState({
    name: '',
    route_type: 'diaria',
    date: format(new Date(), 'yyyy-MM-dd'),
    stops: [],
    assigned_to: ''
  });

  useEffect(() => {
    loadData();
    getCurrentLocation();
  }, []);

  const loadData = async () => {
    try {
      const requests = [
        api().get('/routes'),
        api().get('/contacts')
      ];
      
      // Admin can see team members to assign routes
      if (isAdmin) {
        requests.push(api().get('/team'));
      }
      
      const responses = await Promise.all(requests);
      setRoutes(responses[0].data);
      setContacts(responses[1].data.filter(c => c.lat && c.lng));
      
      if (isAdmin && responses[2]) {
        setTeamMembers(responses[2].data.filter(m => m.role !== 'admin'));
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      // Initial position
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, lng: longitude });
          setMapCenter([latitude, longitude]);
        },
        (error) => {
          console.log('Location error:', error);
        }
      );
      
      // Watch position for proximity alerts
      if (nearbyAlerts) {
        navigator.geolocation.watchPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            setUserLocation({ lat: latitude, lng: longitude });
            checkProximityToClients(latitude, longitude);
          },
          (error) => console.log('Watch error:', error),
          { enableHighAccuracy: true, maximumAge: 30000, timeout: 27000 }
        );
      }
    }
  };

  // Calculate distance between two points (Haversine formula)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c; // Distance in km
  };

  // Check if user is near any client on today's routes
  const checkProximityToClients = (userLat, userLng) => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const todaysRoutes = routes.filter(r => r.date === today);
    
    for (const route of todaysRoutes) {
      for (const stop of route.stops || []) {
        if (stop.lat && stop.lng && !stop.visited) {
          const distance = calculateDistance(userLat, userLng, stop.lat, stop.lng);
          if (distance < 0.5) { // Within 500 meters
            if (!nearbyClient || nearbyClient.client_id !== stop.client_id) {
              setNearbyClient({ ...stop, route_id: route.id, distance: Math.round(distance * 1000) });
              // Show notification
              if (Notification.permission === 'granted') {
                new Notification('EP3D Vendas - Cliente Próximo!', {
                  body: `Está a ${Math.round(distance * 1000)}m de ${stop.client_name}`,
                  icon: '/images/ep3d-logo.png'
                });
              }
              toast.info(`Está próximo de ${stop.client_name}! (${Math.round(distance * 1000)}m)`, {
                duration: 10000
              });
            }
            return;
          }
        }
      }
    }
    setNearbyClient(null);
  };

  // Request notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Open Google Maps with directions
  const openGoogleMaps = (stop) => {
    console.log('Opening Google Maps for:', stop);
    let url;
    
    // Try coordinates first
    if (stop.lat && stop.lng) {
      url = `https://www.google.com/maps/dir/?api=1&destination=${stop.lat},${stop.lng}`;
    } 
    // Try address
    else if (stop.address) {
      url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(stop.address)}`;
    }
    // Try client name as last resort
    else if (stop.client_name) {
      url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(stop.client_name + ' Portugal')}`;
    }
    else {
      toast.error('Sem morada ou coordenadas disponíveis');
      return;
    }
    
    // Open in new tab
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (!newWindow) {
      // If popup blocked, try location change
      window.location.href = url;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (form.stops.length === 0) {
      toast.error('Adicione pelo menos uma paragem à rota');
      return;
    }
    
    try {
      await api().post('/routes', form);
      toast.success('Rota criada!');
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Erro ao criar rota');
    }
  };

  const handleCheckIn = async () => {
    if (!userLocation) {
      toast.error('Não foi possível obter a sua localização');
      return;
    }
    
    try {
      await api().post('/routes/check-in', {
        route_id: selectedRoute.id,
        client_id: checkInClient.client_id,
        lat: userLocation.lat,
        lng: userLocation.lng,
        notes: checkInNotes
      });
      toast.success('Check-in registado!');
      setShowCheckInModal(false);
      setCheckInNotes('');
      loadData();
    } catch (error) {
      toast.error('Erro ao registar check-in');
    }
  };

  const addStop = (contact) => {
    const stop = {
      client_id: contact.id,
      client_name: contact.name,
      address: contact.address,
      lat: contact.lat,
      lng: contact.lng,
      visited: false
    };
    
    setForm({
      ...form,
      stops: [...form.stops, stop]
    });
  };

  const removeStop = (index) => {
    setForm({
      ...form,
      stops: form.stops.filter((_, i) => i !== index)
    });
  };

  const openCheckIn = (route, stop) => {
    setSelectedRoute(route);
    setCheckInClient(stop);
    setShowCheckInModal(true);
    
    // Always try to get location when opening check-in modal
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, lng: longitude });
          toast.success('Localização obtida!');
        },
        (error) => {
          console.log('Check-in location error:', error);
          toast.error('Não foi possível obter a localização. Ative o GPS e tente novamente.');
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    } else {
      toast.error('Geolocalização não suportada neste dispositivo');
    }
  };

  const viewRoute = (route) => {
    setViewingRoute(route);
    setShowViewModal(true);
    // Center map on route
    if (route.stops && route.stops.length > 0) {
      const firstStop = route.stops.find(s => s.lat && s.lng);
      if (firstStop) {
        setMapCenter([firstStop.lat, firstStop.lng]);
      }
    }
  };

  const editRoute = (route) => {
    setEditingRoute({ ...route, stops: [...route.stops] });
    setShowEditModal(true);
  };

  const deleteRoute = async (routeId) => {
    if (!window.confirm('Tem a certeza que deseja eliminar esta rota?')) return;
    
    try {
      await api().delete(`/routes/${routeId}`);
      toast.success('Rota eliminada!');
      loadData();
    } catch (error) {
      toast.error('Erro ao eliminar rota');
    }
  };

  const removeStopFromRoute = async (routeId, clientId) => {
    try {
      await api().delete(`/routes/${routeId}/stops/${clientId}`);
      toast.success('Paragem removida!');
      // Update local state
      if (editingRoute) {
        setEditingRoute({
          ...editingRoute,
          stops: editingRoute.stops.filter(s => s.client_id !== clientId)
        });
      }
      loadData();
    } catch (error) {
      toast.error('Erro ao remover paragem');
    }
  };

  const saveRouteChanges = async () => {
    if (!editingRoute) return;
    
    try {
      await api().patch(`/routes/${editingRoute.id}/stops`, {
        name: editingRoute.name,
        stops: editingRoute.stops
      });
      toast.success('Rota atualizada!');
      setShowEditModal(false);
      setEditingRoute(null);
      loadData();
    } catch (error) {
      toast.error('Erro ao guardar alterações');
    }
  };

  const moveStopUp = (index) => {
    if (index <= 0 || !editingRoute) return;
    const newStops = [...editingRoute.stops];
    [newStops[index - 1], newStops[index]] = [newStops[index], newStops[index - 1]];
    setEditingRoute({ ...editingRoute, stops: newStops });
  };

  const moveStopDown = (index) => {
    if (!editingRoute || index >= editingRoute.stops.length - 1) return;
    const newStops = [...editingRoute.stops];
    [newStops[index], newStops[index + 1]] = [newStops[index + 1], newStops[index]];
    setEditingRoute({ ...editingRoute, stops: newStops });
  };

  const getRouteCoordinates = (route) => {
    if (!route?.stops) return [];
    return route.stops
      .filter(s => s.lat && s.lng)
      .map(s => [s.lat, s.lng]);
  };

  const resetForm = () => {
    setForm({
      name: '',
      route_type: 'diaria',
      date: format(new Date(), 'yyyy-MM-dd'),
      stops: [],
      assigned_to: ''
    });
  };

  // Get all stops from all routes for the map
  const allStops = routes.flatMap(route => 
    route.stops?.map(stop => ({
      ...stop,
      routeId: route.id,
      routeName: route.name,
      routeDate: route.date
    })) || []
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="routes-page">
      {/* Nearby Client Alert */}
      {nearbyClient && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-center justify-between animate-pulse">
          <div className="flex items-center gap-3">
            <BellRing className="h-6 w-6 text-amber-600" />
            <div>
              <p className="font-medium text-amber-800">Cliente Próximo!</p>
              <p className="text-sm text-amber-700">
                Está a <strong>{nearbyClient.distance}m</strong> de <strong>{nearbyClient.client_name}</strong>
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => openGoogleMaps(nearbyClient)}
            >
              <ExternalLink className="h-4 w-4 mr-1" />
              Maps
            </Button>
            <Button 
              size="sm" 
              className="bg-amber-500 hover:bg-amber-600 text-slate-900"
              onClick={() => {
                const route = routes.find(r => r.id === nearbyClient.route_id);
                if (route) openCheckIn(route, nearbyClient);
              }}
            >
              <Navigation className="h-4 w-4 mr-1" />
              Check-in
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="list">Lista</TabsTrigger>
              <TabsTrigger value="map">Mapa</TabsTrigger>
            </TabsList>
          </Tabs>
          
          {/* GPS Alerts Toggle */}
          <Button
            variant={nearbyAlerts ? "default" : "outline"}
            size="sm"
            className={nearbyAlerts ? "bg-emerald-600 hover:bg-emerald-700" : ""}
            onClick={() => {
              setNearbyAlerts(!nearbyAlerts);
              toast.info(nearbyAlerts ? 'Alertas GPS desativados' : 'Alertas GPS ativados');
            }}
            title={nearbyAlerts ? "Desativar alertas de proximidade" : "Ativar alertas de proximidade"}
          >
            {nearbyAlerts ? <BellRing className="h-4 w-4 mr-1" /> : <Bell className="h-4 w-4 mr-1" />}
            GPS {nearbyAlerts ? 'ON' : 'OFF'}
          </Button>
        </div>
        
        <Button 
          className="bg-amber-500 hover:bg-amber-600 text-slate-900"
          onClick={() => { resetForm(); setShowModal(true); }}
          data-testid="new-route-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nova Rota
        </Button>
      </div>

      {activeTab === 'list' ? (
        /* Routes List */
        <div className="space-y-4">
          {routes.length === 0 ? (
            <Card>
              <CardContent className="p-16 text-center">
                <Route className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-semibold font-[Manrope]">Nenhuma rota planeada</h3>
                <p className="text-muted-foreground mt-1">Crie uma rota para planear as suas visitas</p>
                <Button 
                  className="mt-4 bg-amber-500 hover:bg-amber-600 text-slate-900"
                  onClick={() => { resetForm(); setShowModal(true); }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Criar Rota
                </Button>
              </CardContent>
            </Card>
          ) : (
            routes.map((route) => {
              const visitedCount = route.stops?.filter(s => s.visited).length || 0;
              const totalStops = route.stops?.length || 0;
              
              return (
                <Card key={route.id} data-testid={`route-card-${route.id}`}>
                  <CardHeader className="pb-2 px-3 sm:px-6">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-base sm:text-lg font-[Manrope] flex items-center gap-2">
                          <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-amber-500 flex-shrink-0" />
                          <span className="truncate">{route.name}</span>
                        </CardTitle>
                        <div className="flex flex-wrap items-center gap-2 mt-2 text-xs sm:text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                            {format(new Date(route.date), "d MMM", { locale: pt })}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {ROUTE_TYPES.find(t => t.id === route.route_type)?.label}
                          </Badge>
                          <Badge className={`text-xs ${visitedCount === totalStops ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                            {visitedCount}/{totalStops}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button 
                          variant="outline" 
                          size="icon" 
                          className="h-8 w-8 sm:h-9 sm:w-9"
                          onClick={() => viewRoute(route)}
                          title="Ver no mapa"
                          data-testid={`view-route-${route.id}`}
                        >
                          <Map className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="icon"
                          className="h-8 w-8 sm:h-9 sm:w-9"
                          onClick={() => editRoute(route)}
                          title="Editar paragens"
                          data-testid={`edit-route-${route.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8 sm:h-9 sm:w-9 text-red-500 hover:text-red-700 hover:bg-red-50" 
                          onClick={() => deleteRoute(route.id)}
                          title="Eliminar rota"
                          data-testid={`delete-route-${route.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="px-3 sm:px-6">
                    <div className="space-y-2">
                      {route.stops?.slice(0, 5).map((stop, index) => (
                        <div 
                          key={index}
                          className={`flex flex-col sm:flex-row sm:items-center sm:justify-between p-2 sm:p-3 rounded-lg border gap-2 ${stop.visited ? 'bg-emerald-50 border-emerald-200' : 'bg-white'}`}
                        >
                          <div className="flex items-start sm:items-center gap-2 sm:gap-3 min-w-0">
                            {stop.visited ? (
                              <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-500 mt-0.5 sm:mt-0 flex-shrink-0" />
                            ) : (
                              <Circle className="h-4 w-4 sm:h-5 sm:w-5 text-slate-300 mt-0.5 sm:mt-0 flex-shrink-0" />
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-sm sm:text-base truncate">{stop.client_name}</p>
                              {stop.address && (
                                <p className="text-xs text-muted-foreground truncate">{stop.address}</p>
                              )}
                              {stop.visited_at && (
                                <p className="text-xs text-emerald-600">
                                  Visitado às {format(new Date(stop.visited_at), "HH:mm", { locale: pt })}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1 pl-6 sm:pl-0 flex-shrink-0">
                            {/* WhatsApp Button */}
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="h-8 text-xs sm:text-sm text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Get client phone from contacts or stop data
                                const phone = stop.phone || stop.client_phone;
                                if (phone) {
                                  openWhatsApp(phone, WhatsAppTemplates.visitingToday(stop.client_name));
                                } else {
                                  toast.error('Cliente não tem número de telefone');
                                }
                              }}
                              title="Enviar WhatsApp"
                              data-testid={`whatsapp-btn-${stop.client_id}`}
                            >
                              <WhatsAppIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Button>
                            {/* Google Maps Button */}
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="h-8 text-xs sm:text-sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                openGoogleMaps(stop);
                              }}
                              title="Abrir no Google Maps"
                              data-testid={`maps-btn-${stop.client_id}`}
                            >
                              <ExternalLink className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                              Maps
                            </Button>
                            {/* Check-in Button */}
                            {!stop.visited && (
                              <Button 
                                size="sm" 
                                className="h-8 text-xs sm:text-sm bg-amber-500 hover:bg-amber-600 text-slate-900"
                                onClick={() => openCheckIn(route, stop)}
                                data-testid={`checkin-btn-${stop.client_id}`}
                              >
                                <Navigation className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                                Check-in
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                      {route.stops?.length > 5 && (
                        <p className="text-sm text-muted-foreground text-center py-2">
                          +{route.stops.length - 5} mais paragens
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      ) : (
        /* Map View */
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <div className="h-[600px]">
              <MapContainer 
                center={mapCenter} 
                zoom={8} 
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapController center={mapCenter} />
                
                {/* User location marker */}
                {userLocation && (
                  <Marker position={[userLocation.lat, userLocation.lng]}>
                    <Popup>
                      <strong>A sua localização</strong>
                    </Popup>
                  </Marker>
                )}
                
                {/* Route stops markers */}
                {allStops.map((stop, index) => (
                  stop.lat && stop.lng && (
                    <Marker 
                      key={`${stop.routeId}-${index}`}
                      position={[stop.lat, stop.lng]}
                      icon={stop.visited ? visitedIcon : pendingIcon}
                    >
                      <Popup>
                        <div className="text-sm">
                          <strong>{stop.client_name}</strong>
                          <p className="text-muted-foreground">{stop.routeName}</p>
                          <p className="text-xs">{format(new Date(stop.routeDate), "d MMM", { locale: pt })}</p>
                          {stop.visited ? (
                            <Badge className="mt-1 bg-emerald-100 text-emerald-800">Visitado</Badge>
                          ) : (
                            <Badge className="mt-1 bg-amber-100 text-amber-800">Pendente</Badge>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  )
                ))}
              </MapContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Route Modal */}
      <Dialog open={showModal} onOpenChange={(open) => { if (!open) resetForm(); setShowModal(open); }}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Nova Rota</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome da Rota *</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex: Rota Lisboa Norte - Semana 5"
                  required
                  data-testid="route-name-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <Select value={form.route_type} onValueChange={(v) => setForm({ ...form, route_type: v })}>
                    <SelectTrigger data-testid="route-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROUTE_TYPES.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date">Data</Label>
                  <Input
                    id="date"
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    required
                    data-testid="route-date-input"
                  />
                </div>
              </div>

              {/* Admin can assign route to team member */}
              {isAdmin && teamMembers.length > 0 && (
                <div className="space-y-2">
                  <Label>Atribuir a</Label>
                  <Select value={form.assigned_to || "self"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "self" ? "" : v })}>
                    <SelectTrigger data-testid="route-assign-select">
                      <SelectValue placeholder="Selecionar vendedor (opcional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="self">Eu próprio</SelectItem>
                      {teamMembers.map((member) => (
                        <SelectItem key={member.id} value={member.id}>
                          {member.name} ({member.role === 'gestor' ? 'Gestor' : 'Vendedor'})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Como admin, pode criar rotas para vendedores e gestores
                  </p>
                </div>
              )}

              {/* Add Clients */}
              <div className="space-y-2">
                <Label>Adicionar Clientes à Rota</Label>
                <div className="max-h-[200px] overflow-y-auto border rounded-lg">
                  {contacts.length === 0 ? (
                    <p className="p-4 text-sm text-muted-foreground text-center">
                      Nenhum cliente com localização definida
                    </p>
                  ) : (
                    contacts.map((contact) => {
                      const isAdded = form.stops.some(s => s.client_id === contact.id);
                      return (
                        <div 
                          key={contact.id}
                          className={`flex items-center justify-between p-3 border-b last:border-b-0 ${isAdded ? 'bg-emerald-50' : ''}`}
                        >
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="text-sm font-medium">{contact.name}</p>
                              {contact.address && (
                                <p className="text-xs text-muted-foreground">{contact.address}</p>
                              )}
                            </div>
                          </div>
                          {isAdded ? (
                            <Badge className="bg-emerald-100 text-emerald-800">Adicionado</Badge>
                          ) : (
                            <Button 
                              type="button" 
                              size="sm" 
                              variant="outline"
                              onClick={() => addStop(contact)}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Selected Stops */}
              {form.stops.length > 0 && (
                <div className="space-y-2">
                  <Label>Paragens na Rota ({form.stops.length})</Label>
                  <div className="space-y-2">
                    {form.stops.map((stop, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                        <span className="text-sm font-medium">{index + 1}. {stop.client_name}</span>
                        <Button type="button" variant="ghost" size="icon" className="text-red-500" onClick={() => removeStop(index)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="save-route-btn">
                Criar Rota
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Check-in Modal */}
      <Dialog open={showCheckInModal} onOpenChange={setShowCheckInModal}>
        <DialogContent className="w-[95vw] max-w-[400px] rounded-lg">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Check-in</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="font-medium">{checkInClient?.client_name}</p>
              {checkInClient?.address && (
                <p className="text-sm text-muted-foreground">{checkInClient.address}</p>
              )}
            </div>
            
            {userLocation ? (
              <div className="flex items-center gap-2 text-sm text-emerald-600 p-3 bg-emerald-50 rounded-lg">
                <Navigation className="h-4 w-4" />
                <span>Localização obtida ({userLocation.lat.toFixed(4)}, {userLocation.lng.toFixed(4)})</span>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-amber-600 p-3 bg-amber-50 rounded-lg">
                  <Clock className="h-4 w-4 animate-pulse" />
                  <span>A obter localização...</span>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full"
                  onClick={() => {
                    if (navigator.geolocation) {
                      toast.info('A obter localização...');
                      navigator.geolocation.getCurrentPosition(
                        (position) => {
                          const { latitude, longitude } = position.coords;
                          setUserLocation({ lat: latitude, lng: longitude });
                          toast.success('Localização obtida!');
                        },
                        (error) => {
                          console.log('Manual location error:', error);
                          if (error.code === 1) {
                            toast.error('Permissão de localização negada. Ative nas definições do browser.');
                          } else if (error.code === 2) {
                            toast.error('Localização não disponível. Verifique o GPS.');
                          } else if (error.code === 3) {
                            toast.error('Tempo esgotado. Tente novamente.');
                          }
                        },
                        { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
                      );
                    }
                  }}
                  data-testid="retry-location-btn"
                >
                  <Navigation className="h-4 w-4 mr-2" />
                  Tentar Obter Localização
                </Button>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="check-in-notes">Notas da visita</Label>
              <Textarea
                id="check-in-notes"
                rows={3}
                value={checkInNotes}
                onChange={(e) => setCheckInNotes(e.target.value)}
                placeholder="Resumo da visita..."
                data-testid="checkin-notes-input"
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button type="button" variant="outline" onClick={() => setShowCheckInModal(false)} className="w-full sm:w-auto">
              Cancelar
            </Button>
            <Button 
              onClick={handleCheckIn}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 w-full sm:w-auto"
              disabled={!userLocation}
              data-testid="confirm-checkin-btn"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Confirmar Check-in
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Route on Map Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="font-[Manrope] flex items-center gap-2">
              <Map className="h-5 w-5 text-amber-500" />
              {viewingRoute?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Route info */}
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {viewingRoute && format(new Date(viewingRoute.date), "d 'de' MMMM yyyy", { locale: pt })}
              </span>
              <Badge variant="outline">
                {viewingRoute?.stops?.length} paragens
              </Badge>
            </div>
            
            {/* Map */}
            <div className="h-[400px] rounded-lg overflow-hidden border">
              {viewingRoute && (
                <MapContainer
                  center={mapCenter}
                  zoom={10}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; OpenStreetMap'
                  />
                  <MapController center={mapCenter} />
                  
                  {/* Route line */}
                  {getRouteCoordinates(viewingRoute).length > 1 && (
                    <Polyline
                      positions={getRouteCoordinates(viewingRoute)}
                      color="#f59e0b"
                      weight={3}
                      opacity={0.8}
                    />
                  )}
                  
                  {/* Numbered markers */}
                  {viewingRoute.stops?.filter(s => s.lat && s.lng).map((stop, index) => (
                    <Marker
                      key={stop.client_id}
                      position={[stop.lat, stop.lng]}
                      icon={stop.client_id === 'headquarters' || stop.client_id === 'headquarters_return' 
                        ? headquartersIcon 
                        : numberIcon(index + 1)}
                    >
                      <Popup>
                        <div className="text-sm">
                          <p className="font-medium">{index + 1}. {stop.client_name}</p>
                          {stop.address && <p className="text-muted-foreground">{stop.address}</p>}
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              )}
            </div>
            
            {/* Stops list */}
            <ScrollArea className="h-[200px] border rounded-lg p-3">
              <div className="space-y-2">
                {viewingRoute?.stops?.map((stop, index) => (
                  <div key={stop.client_id} className="flex items-center gap-3 p-2 bg-slate-50 rounded">
                    <span className="w-6 h-6 flex items-center justify-center bg-amber-500 text-white text-xs font-bold rounded-full">
                      {index + 1}
                    </span>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{stop.client_name}</p>
                      {stop.address && <p className="text-xs text-muted-foreground">{stop.address}</p>}
                    </div>
                    {stop.visited && <CheckCircle className="h-4 w-4 text-emerald-500" />}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowViewModal(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Route Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="font-[Manrope] flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Editar Rota
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Route name edit */}
            <div className="space-y-2">
              <Label>Nome da Rota</Label>
              <Input
                value={editingRoute?.name || ''}
                onChange={(e) => setEditingRoute({...editingRoute, name: e.target.value})}
              />
            </div>
            
            <div className="border-t pt-4">
              <Label className="text-sm font-medium mb-2 block">Paragens ({editingRoute?.stops?.length || 0})</Label>
              <p className="text-xs text-muted-foreground mb-3">
                Arraste para reordenar ou remova paragens que não pretende visitar
              </p>
              
              <ScrollArea className="h-[350px] border rounded-lg p-3">
                <div className="space-y-2">
                  {editingRoute?.stops?.map((stop, index) => (
                    <div 
                      key={stop.client_id} 
                      className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border"
                    >
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => moveStopUp(index)}
                          disabled={index === 0}
                        >
                          <span className="text-xs">▲</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => moveStopDown(index)}
                          disabled={index === editingRoute.stops.length - 1}
                        >
                          <span className="text-xs">▼</span>
                        </Button>
                      </div>
                      
                      <span className="w-7 h-7 flex items-center justify-center bg-amber-500 text-white text-sm font-bold rounded-full">
                        {index + 1}
                      </span>
                      
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{stop.client_name}</p>
                        {stop.address && <p className="text-xs text-muted-foreground truncate">{stop.address}</p>}
                      </div>
                      
                      {stop.visited && (
                        <Badge className="bg-emerald-100 text-emerald-800 text-xs">Visitado</Badge>
                      )}
                      
                      {!stop.client_id?.includes('headquarters') && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            setEditingRoute({
                              ...editingRoute,
                              stops: editingRoute.stops.filter((_, i) => i !== index)
                            });
                          }}
                          title="Remover paragem"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancelar
            </Button>
            <Button 
              className="bg-amber-500 hover:bg-amber-600 text-slate-900"
              onClick={saveRouteChanges}
            >
              Guardar Alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
